import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { DDPoint } from "haversine-ts";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

import {
  LOCATION_FINDER_CONTEXT_DEFAULT,
  LOCATION_FINDER_MAP_CENTER,
  LOCATION_FINDER_MAP_RESTRICTION,
  MAP_ID,
} from "./constants";
import { LocationFinderContextProps, LocationSpecialHours } from "./types";
import { createMarkerFn, CustomRenderer } from "./utils";

import { BRAND_BOOK_COLORS } from "~/Constant/Theme/configs/equipmentshare";
import { useReduxAuth } from "~/hooks/useReduxAuth";
import {
  useOmnichannelApi,
  useOmnichannelApiWithReduxAuth,
  useOmnichannelApiWithReduxAuthIsReady,
} from "~/OmnichannelApi";
import { Components, Paths } from "~/OmnichannelApi/openapi";
import { googleLoader, HaversineMiles } from "~/utils";

export const LocationFinderContext = createContext<LocationFinderContextProps>(
  LOCATION_FINDER_CONTEXT_DEFAULT
);

export const LocationFinderProvider: React.FC = ({ children }) => {
  const api = useOmnichannelApi();
  const authApi = useOmnichannelApiWithReduxAuth();
  const authApiReady = useOmnichannelApiWithReduxAuthIsReady();
  const reduxAuth = useReduxAuth();

  const [userGeo, setUserGeo] = useState<Paths.Geolocate.Responses.$200>();
  const [locationData, setLocationData] =
    useState<Paths.LocationFinderSearch.Responses.$200>();
  const [filteredLocations, setFilteredLocations] =
    useState<Array<Components.Schemas.LocationFinderLocation>>();
  const [locationSpecialHours, setLocationSpecialHours] =
    useState<Array<LocationSpecialHours>>();
  const [locationSearchOptions, setLocationSearchOptions] =
    useState<Paths.LocationFinderSearch.RequestBody>({
      aggs: [
        "location.markets.solution.slug",
        "location.markets.solution.name",
        "location.markets.rental_services.name",
      ],
    });
  const [mapsLib, setMapsLib] = useState<google.maps.MapsLibrary>();
  const [markerLib, setMarkerLib] = useState<google.maps.MarkerLibrary>();
  const [mountedMap, setMountedMap] = useState<google.maps.Map>();
  const [isMapMounted, setIsMapMounted] = useState<boolean>(false);
  const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer>();
  const [markers, _setMarkers] = useState<
    Array<google.maps.marker.AdvancedMarkerElement>
  >([]);
  const [activeMarkerSlug, setActiveMarkerSlug] = useState<string>();
  const [activeMarkerInfoWindow, setActiveMarkerInfoWindow] = useState<
    google.maps.InfoWindow | undefined
  >();
  const [activeFilterTypeValue, setActiveFilterTypeValue] = useState<
    string | undefined
  >();
  const [distanceCenter, setDistanceCenter] =
    useState<google.maps.LatLngLiteral>(LOCATION_FINDER_MAP_CENTER);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);

  const setMarkers = useCallback(
    (newMarkers: Array<google.maps.marker.AdvancedMarkerElement>) => {
      if (markerClusterer) {
        markerClusterer.removeMarkers(markers, true);
        markerClusterer.addMarkers(newMarkers, true);
        markerClusterer.render();
      }

      _setMarkers(newMarkers);
    },
    [markerClusterer, markers, _setMarkers]
  );

  // load Google libraries on mount
  useEffect(() => {
    Promise.all([
      googleLoader.importLibrary("maps"),
      googleLoader.importLibrary("marker"),
      window.getGeo!(),
    ]).then(([mapsLibInstance, markerLibInstance, userGeoRes]) => {
      setDistanceCenter({
        lat: Number(userGeoRes.latitude),
        lng: Number(userGeoRes.longitude),
      });
      setUserGeo(userGeoRes);
      setMapsLib(mapsLibInstance);
      setMarkerLib(markerLibInstance);
    });
  }, []);

  // mount the Map when library is avail
  useEffect(() => {
    if (!mountedMap && mapsLib && userGeo) {
      const el = document.getElementById(MAP_ID);

      if (el) {
        const mountedMap = new mapsLib.Map(el, {
          mapId: MAP_ID,
          gestureHandling: "cooperative",
          center: LOCATION_FINDER_MAP_CENTER,
          zoom: 4,
          isFractionalZoomEnabled: true,
          restriction: LOCATION_FINDER_MAP_RESTRICTION,
          mapTypeControl: null,
          streetViewControl: false,
          fullscreenControl: false,
        });

        setMountedMap(mountedMap);
        setIsMapMounted(true);
      } else {
        console.error(`Unable to find an element with id="${MAP_ID}"`);
      }
    }
  }, [mountedMap, mapsLib, userGeo]);

  // add MarkerClusterer after Map is mounted
  useEffect(() => {
    if (!markerClusterer && mountedMap && locationData) {
      setMarkerClusterer(
        new MarkerClusterer({
          markers,
          map: mountedMap,
          renderer: new CustomRenderer(),
          onClusterClick: (evt, cluster, map) => {
            map.setCenter(cluster.position);
            map.setZoom((map.getZoom() || 0) + 1);
          },
        })
      );
    }
  }, [markerClusterer, mountedMap, locationData]);

  // fetch locations when api auth is finished loading
  useEffect(() => {
    // console.info(reduxAuth);

    if (reduxAuth?.isLoading === false) {
      let apiClient = api;

      if (reduxAuth.isAuthenticated === true) {
        if (!authApiReady) {
          // wait until ready
          return;
        }

        apiClient = authApi!;
      }

      Promise.all([
        api["static.webflow.collections.items.get"]({
          collection_slug: "location-special-hours",
          path_name: "items",
          item_slug: "_all",
          file_type: "json",
        }),
        apiClient["location-finder.search"](undefined, locationSearchOptions),
      ]).then(([specialHoursRes, locationsRes]) => {
        setLocationData(locationsRes.data);
        setLocationSpecialHours(
          specialHoursRes.data as Array<LocationSpecialHours>
        );
      });
    }
  }, [authApiReady, reduxAuth, locationSearchOptions]);

  useEffect(() => {
    const refPoint = new DDPoint(distanceCenter.lat, distanceCenter.lng);

    const sortedFilteredLocations = locationData?.locations
      .filter((loc) => {
        if (!activeFilterTypeValue || activeFilterTypeValue === "_all") {
          return true;
        }

        const terms = activeFilterTypeValue.split(":");

        if (terms.length === 2) {
          const [filterType, filterVal] = terms;
          if (filterType === "solution") {
            return loc.markets.some((m) => m.solution.name === filterVal);
          }
          if (filterType === "rental_service") {
            return loc.markets.some((m) =>
              m.rental_services?.some((rs) => rs.name === filterVal)
            );
          }
        }
      })
      .sort((a, b) =>
        HaversineMiles.getDistance(
          refPoint,
          new DDPoint(a.geo.latitude, a.geo.longitude)
        ) <
        HaversineMiles.getDistance(
          refPoint,
          new DDPoint(b.geo.latitude, b.geo.longitude)
        )
          ? -1
          : 1
      );

    setFilteredLocations(sortedFilteredLocations);
  }, [locationData, activeFilterTypeValue, distanceCenter]);

  // redraw map when locationData changes
  // isMapMounted ensures we only re-render when the map goes from unmounted => mounted
  // otherwise using mapMounted will create render loops
  useEffect(() => {
    if (
      locationData &&
      isMapMounted &&
      mapsLib &&
      markerLib &&
      filteredLocations
    ) {
      setMarkers(
        filteredLocations?.map(
          createMarkerFn({
            mapsLib,
            markerLib,
            mountedMap,
            setActiveMarkerSlug,
            setActiveMarkerInfoWindow,
          })
        )
      );
    }
  }, [filteredLocations, markerClusterer, isMapMounted]);

  const panMapTo: LocationFinderContextProps["panMapTo"] = (
    latlng,
    zoom = 8
  ) => {
    if (mountedMap) {
      // zoom in only--don't zoom out
      const currentZoom = mountedMap.getZoom();
      if (currentZoom && currentZoom < zoom) {
        mountedMap.setZoom(zoom);
      }

      mountedMap.panTo(latlng);
    } else {
      console.error("Unable to panMapTo(): mountedMap is not defined");
    }
  };

  const [zoomOut, setZoomOut] = useState<boolean>(false);
  useEffect(() => {
    if (mountedMap && filteredLocations?.length) {
      const targetLocation = filteredLocations.at(0)!;

      const targetLocationLatlon = {
        lat: targetLocation.geo.latitude,
        lng: targetLocation.geo.longitude,
      };

      if (
        !zoomOut &&
        !mountedMap?.getBounds()?.contains(targetLocationLatlon)
      ) {
        setTimeout(() => setZoomOut(true), 1000);
      } else if (zoomOut) {
        while (!mountedMap?.getBounds()?.contains(targetLocationLatlon)) {
          mountedMap.setZoom(mountedMap.getZoom()! - 0.25);
        }

        setZoomOut(false);
      }
    }
  }, [filteredLocations, zoomOut]);

  useEffect(() => {
    if (
      activeMarkerInfoWindow &&
      (activeMarkerInfoWindow as { activeMarkerSlug?: string })
        .activeMarkerSlug !== activeMarkerSlug
    ) {
      activeMarkerInfoWindow.close();
      setActiveMarkerInfoWindow(undefined);
    }
  }, [activeMarkerSlug]);

  return (
    <React.Fragment>
      {activeMarkerSlug && (
        <style>{css`
          svg.marker-clusterer:has([data-location-slug="${activeMarkerSlug}"]) {
            fill: ${BRAND_BOOK_COLORS.ORANGE_PRIMARY};
            & * {
              opacity: 1 !important;
            }
          }
          svg.marker[data-location-slug="${activeMarkerSlug}"] path {
            fill: ${BRAND_BOOK_COLORS.ORANGE_PRIMARY};
          }
        `}</style>
      )}
      <LocationFinderContext.Provider
        value={{
          userGeo,
          locationData,
          filteredLocations,
          locationSpecialHours,
          setLocationSearchOptions,
          mapsLib,
          markerLib,
          mountedMap,
          setMountedMap,
          markerClusterer,
          setMarkerClusterer,
          activeMarkerSlug,
          setActiveMarkerSlug,
          activeMarkerInfoWindow,
          setActiveMarkerInfoWindow,
          activeFilterTypeValue,
          setActiveFilterTypeValue,
          distanceCenter,
          setDistanceCenter,
          isDrawerOpen,
          setIsDrawerOpen,
          panMapTo,
        }}
      >
        {children}
      </LocationFinderContext.Provider>
    </React.Fragment>
  );
};
